import React, { useRef, useEffect } from "react";
import "./PayoutHistoryComponent.css";
import { FiSearch, FiCalendar, FiX, FiArrowLeft, FiTrash2, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';
import { BsClipboardData } from 'react-icons/bs';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';

// Delete Confirmation Modal
const DeleteModal = ({ show, filename, onConfirm, onCancel }) => {
  if (!show) return null;

  return (
    <div className="ph-modal-overlay" >
      <div className="ph-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="ph-modal-header">
          <FiAlertCircle className="ph-modal-icon ph-warning-icon" />

          {/* <button className="ph-modal-close" onClick={onCancel}>
            <FiX />
          </button> */}
        </div>
        <div className="ph-modal-body">
          <p>Are you sure you want to delete this payout record?</p>
          <p className="ph-modal-filename">File: <strong>{filename}</strong></p>
          <p className="ph-modal-warning">Once deleted, this file and its data cannot be recovered.</p>
        </div>
        <div className="ph-modal-footer">
          <button className="ph-modal-cancel" onClick={onCancel}>
            Cancel
          </button>
          <button className="ph-modal-confirm ph-delete-confirm" onClick={onConfirm}>
            Yes, Delete
          </button>
        </div>
      </div>
    </div>
  );
};

// Success Popup
const SuccessPopup = ({ show, message, onClose }) => {
  if (!show) return null;

  return (
    <div className="ph-modal-overlay" onClick={onClose}>
      <div className="ph-modal-container ph-success-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ph-modal-header">
          <FiCheckCircle className="ph-modal-icon ph-success-icon" />


        </div>
        <h3 style={{ textAlign: "center", fontSize: "18px" }}>Deleted Successful</h3>
        <div className="ph-modal-body">
          <p>File has been Deleted Successfully</p>
        </div>
        <div className="ph-modal-footer">
          <button className="ph-modal-confirm" onClick={onClose}>
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

const PayoutHistoryComponent = ({
  uploads,
  loading,
  error,
  searchTerm,
  onSearchChange,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  showDateFilter,
  onToggleDateFilter,
  onClearDateFilter,
  onUploadNew,
  formatDisplayDate,
  onDeleteClick,
  deleteModal,
  onDeleteConfirm,
  onDeleteCancel,
  successPopup,
  onSuccessPopupClose
}) => {
  const navigate = useNavigate();
  const dateFilterRef = useRef(null);
  const handleBack = () => navigate("/home");
  // Close date filter when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dateFilterRef.current && !dateFilterRef.current.contains(event.target)) {
        if (showDateFilter) onToggleDateFilter();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDateFilter, onToggleDateFilter]);

  const handleCopy = (id) => {
    navigator.clipboard.writeText(id.toString());
    // You can add a small toast notification here if needed
  };

  if (loading) {
    return (
      <div className="ph-page-wrapper">
        <div className="ph-loading-container">
          <div className="ph-spinner"></div>
          <p>Loading payout history...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ph-page-wrapper">
      {/* Header */}
      <div className="ph-header">
        <button className="ph-back-button" onClick={handleBack}>
          <FiArrowLeft className="dfPayout-back-icon" />
        </button>
        <h1 className="ph-title">Payout History</h1>
      </div>

      {/* Search and Filter Section */}
      <div className="ph-search-section">
        <div className="ph-search-wrapper">
          <FiSearch className="ph-search-icon" />
          <input
            type="text"
            className="ph-search-input"
            placeholder="Search by ID or file name"
            value={searchTerm}
            onChange={onSearchChange}
          />
        </div>

        <div className="ph-filter-wrapper" ref={dateFilterRef}>
          <button
            className={`ph-filter-btn ${showDateFilter ? 'active' : ''}`}
            onClick={onToggleDateFilter}
          >
            <FiCalendar className="ph-filter-icon" />
            <span>Filter by Date</span>
          </button>

          {showDateFilter && (
            <div className="ph-date-filter-popup">
              <div className="ph-date-filter-header">
                <span>Select Date Range</span>
                <button className="ph-date-filter-close" onClick={onToggleDateFilter}>
                  <FiX />
                </button>
              </div>
              <div className="ph-date-filter-body">
                <div className="ph-date-input-group">
                  <label>From</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={onStartDateChange}
                    max={endDate || undefined}
                  />
                </div>
                <div className="ph-date-input-group">
                  <label>To</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={onEndDateChange}
                    min={startDate || undefined}
                  />
                </div>
              </div>
              {(startDate || endDate) && (
                <div className="ph-date-filter-footer">
                  <button className="ph-clear-filter" onClick={onClearDateFilter}>
                    Clear Filter
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <button className="ph-upload-btn" onClick={onUploadNew}>
          + Upload New
        </button>
      </div>

      {/* Active Filters */}
      {(startDate || endDate) && (
        <div className="ph-active-filters">
          <span className="ph-active-filter-label">Active Filters:</span>
          {startDate && (
            <span className="ph-filter-tag">
              From: {moment(startDate).format('DD MMM YYYY')}
              <button onClick={() => onStartDateChange({ target: { value: '' } })}>
                <FiX />
              </button>
            </span>
          )}
          {endDate && (
            <span className="ph-filter-tag">
              To: {moment(endDate).format('DD MMM YYYY')}
              <button onClick={() => onEndDateChange({ target: { value: '' } })}>
                <FiX />
              </button>
            </span>
          )}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="ph-error-message">
          <FiAlertCircle className="ph-error-icon" />
          <span>{error}</span>
        </div>
      )}

      {/* Table */}
      <div className="ph-table-container">
        <table className="ph-table">
          <thead>
            <tr>
              <th>S.No.</th>
              <th>Payout Date</th>
              <th>Uploaded ID</th>
              <th>File Name</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {uploads.length > 0 ? (
              uploads.map((upload, index) => (
                <tr key={upload.id}>
                  <td className="ph-sno">{index + 1}</td>
                  <td className="ph-date">{formatDisplayDate(upload.payoutDate)}</td>
                  <td className="ph-id">{upload.id}</td>
                  <td className="ph-filename">
                    <BsClipboardData className="ph-file-icon" />
                    {upload.filename}
                  </td>
                  <td className="ph-status">
                    <span className="ph-status-badge ph-status-settled">
                      Settled
                    </span>
                  </td>
                  <td className="ph-actions">

                    <button
                      className="ph-action-btn ph-delete-btn"
                      onClick={() => onDeleteClick(upload.id, upload.filename)}
                      title="Delete"
                    >
                      <FiTrash2 />
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="ph-no-data">
                  <div className="ph-empty-state">
                    <BsClipboardData className="ph-empty-icon" />
                    <p>No payout records found</p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modals */}
      <DeleteModal
        show={deleteModal.show}
        filename={deleteModal.filename}
        onConfirm={onDeleteConfirm}
        onCancel={onDeleteCancel}
      />

      <SuccessPopup
        show={successPopup.show}
        message={successPopup.message}
        onClose={onSuccessPopupClose}
      />
    </div>
  );
};

export default PayoutHistoryComponent;